import type { AriaToolId } from "./api.ts";

// Client-side mirror of tools/aria_persona_config.py's enumerations and
// TOOL_CATALOGUE, plus display labels the backend has no reason to know
// about. Keep values in sync with the Python module -- the server is the
// source of truth for what's *valid*; this file only adds *labels*.

export const PRONOUNS = ["she/her", "he/him", "they/them", "none"] as const;
export const AVATAR_MARKS = [
  { value: "initials", label: "Initials" },
  { value: "glyph", label: "Glyph" },
  { value: "photo", label: "Photo" },
] as const;
export const GREET_STYLES = ["warm", "brief", "formal", "none"] as const;
export const EMOJI_POLICIES = ["never", "sparing", "free"] as const;
export const HUMOUR_LEVELS = ["none", "light", "playful"] as const;
export const SELF_DISCLOSE = [
  { value: "always", label: "Always" },
  { value: "on-ask", label: "On ask" },
  { value: "footer", label: "Footer only" },
] as const;

export const TONE_PRESETS = [
  { value: "concise", label: "Concise" },
  { value: "concierge", label: "Concierge" },
  { value: "formal", label: "Formal" },
  { value: "playful", label: "Playful" },
  { value: "technical", label: "Technical" },
  { value: "clinical", label: "Clinical" },
  { value: "custom", label: "Custom" },
] as const;
export const READING_LEVELS = [
  { value: "grade6", label: "Grade 6" },
  { value: "grade8", label: "Grade 8" },
  { value: "grade10", label: "Grade 10" },
  { value: "pro", label: "Pro" },
] as const;
export const SENTENCE_LENGTHS = ["short", "mixed", "long"] as const;
export const SPELLING = ["us", "uk", "auto"] as const;

/* The 42 languages the orchestrator actually serves. src/playground/languages.py
   is the source of truth -- the /options endpoint serves that list and
   language_label() resolves a code for the prompt directive. Labels carry the
   endonym because an operator picking languages recognises 日本語, not "ja".
   Codes and order match that file; keep them in step. */
export const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español (Spanish)" },
  { value: "fr", label: "Français (French)" },
  { value: "de", label: "Deutsch (German)" },
  { value: "it", label: "Italiano (Italian)" },
  { value: "pt", label: "Português (Portuguese)" },
  { value: "nl", label: "Nederlands (Dutch)" },
  { value: "sv", label: "Svenska (Swedish)" },
  { value: "no", label: "Norsk (Norwegian)" },
  { value: "da", label: "Dansk (Danish)" },
  { value: "fi", label: "Suomi (Finnish)" },
  { value: "pl", label: "Polski (Polish)" },
  { value: "cs", label: "Čeština (Czech)" },
  { value: "sk", label: "Slovenčina (Slovak)" },
  { value: "hu", label: "Magyar (Hungarian)" },
  { value: "ro", label: "Română (Romanian)" },
  { value: "el", label: "Ελληνικά (Greek)" },
  { value: "bg", label: "Български (Bulgarian)" },
  { value: "uk", label: "Українська (Ukrainian)" },
  { value: "ru", label: "Русский (Russian)" },
  { value: "tr", label: "Türkçe (Turkish)" },
  { value: "ar", label: "العربية (Arabic)" },
  { value: "he", label: "עברית (Hebrew)" },
  { value: "fa", label: "فارسی (Persian)" },
  { value: "ur", label: "اردو (Urdu)" },
  { value: "hi", label: "हिन्दी (Hindi)" },
  { value: "bn", label: "বাংলা (Bengali)" },
  { value: "ta", label: "தமிழ் (Tamil)" },
  { value: "te", label: "తెలుగు (Telugu)" },
  { value: "th", label: "ไทย (Thai)" },
  { value: "vi", label: "Tiếng Việt (Vietnamese)" },
  { value: "id", label: "Bahasa Indonesia (Indonesian)" },
  { value: "ms", label: "Bahasa Melayu (Malay)" },
  { value: "tl", label: "Filipino" },
  { value: "ja", label: "日本語 (Japanese)" },
  { value: "ko", label: "한국어 (Korean)" },
  { value: "zh", label: "中文 (Chinese)" },
  { value: "zh-Hant", label: "繁體中文 (Traditional Chinese)" },
  { value: "sw", label: "Kiswahili (Swahili)" },
  { value: "ca", label: "Català (Catalan)" },
  { value: "hr", label: "Hrvatski (Croatian)" },
  { value: "sr", label: "Српски (Serbian)" },
] as const;
export const LANGUAGE_CODES = LANGUAGES.map((l) => l.value);

/** Label a saved list of codes for a picker. An unrecognised code keeps its own
 *  text rather than disappearing, so a tenant can still see -- and change --
 *  a language that left the catalogue. */
export function languageOptions(codes: readonly string[]): { value: string; label: string }[] {
  return codes.map((code) => {
    const known = LANGUAGES.find((l) => l.value === code);
    return { value: code, label: known ? known.label : code };
  });
}
export const LANG_FALLBACK = [
  ...LANGUAGES,
  { value: "ask", label: "Ask the visitor" },
  { value: "human", label: "Hand to a human" },
] as const;
export const DATE_FORMATS = ["locale", "iso", "us"] as const;
export const UNIT_SYSTEMS = ["imperial", "metric", "locale"] as const;
export const PROFANITY_POLICIES = [
  { value: "mirror-neutral", label: "Mirror neutrally" },
  { value: "acknowledge", label: "Acknowledge" },
  { value: "escalate", label: "Escalate" },
] as const;

export const PROACTIVITY = [
  { value: "reactive", label: "Reactive" },
  { value: "balanced", label: "Balanced" },
  { value: "anticipatory", label: "Anticipatory" },
] as const;
export const ACT_POLICIES = [
  { value: "ask-then-act", label: "Ask, then act" },
  { value: "act-then-tell", label: "Act, then tell" },
  { value: "ask-always", label: "Always ask" },
] as const;
export const FOLLOWUP_POLICIES = ["none", "once", "twice"] as const;
export const CLOSEOUT_STYLES = [
  { value: "summary", label: "Summary" },
  { value: "csat", label: "CSAT" },
  { value: "both", label: "Both" },
  { value: "none", label: "None" },
] as const;

export const KNOWLEDGE_SOURCES = [
  "help centre", "policies", "order data", "past resolutions",
  "product catalogue", "pricing sheet", "release notes", "internal runbooks",
] as const;
export const CITATION_POLICIES = ["always", "on-policy", "never"] as const;
export const UNKNOWN_POLICIES = [
  { value: "admit-and-route", label: "Admit & route" },
  { value: "admit-and-ticket", label: "Admit & ticket" },
  { value: "best-effort", label: "Best effort" },
] as const;

export const JAILBREAK_LEVELS = ["standard", "high", "paranoid"] as const;
export const PII_POLICIES = ["redact", "tokenise", "store"] as const;
export const MARKDOWN_POLICIES = [
  { value: "links-lists", label: "Links & lists" },
  { value: "full", label: "Full markdown" },
  { value: "plain", label: "Plain text" },
] as const;

export const ESCALATION_TRIGGERS = [
  "low confidence", "negative sentiment", "explicit ask", "repeat contact",
  "refund over cap", "VIP tier", "legal language", "chargeback", "third failed answer",
] as const;
export const ROUTING_MODES = [
  { value: "skill-based", label: "Skill-based" },
  { value: "round-robin", label: "Round robin" },
  { value: "owner", label: "Account owner" },
  { value: "queue", label: "Shared queue" },
] as const;
export const AFTER_HOURS_POLICIES = ["ticket", "callback", "queue"] as const;

export const MEMORY_FIELDS = [
  "name", "order history", "preferences", "language",
  "tone", "open issues", "address", "payment method",
] as const;

export const VOICE_PROFILES = ["ember", "slate", "reed", "linen", "granite"] as const;
export const HOLD_AUDIO = ["ambient", "music", "silence"] as const;

// Tier names, not dated model ids -- see the MODEL_IDS note in
// tools/aria_persona_config.py. The stored value is what the operator picked;
// the backend resolves it to the current id at call time.
export const MODEL_PRIMARY = [
  { value: "haiku", label: "Haiku · fastest" },
  { value: "sonnet", label: "Sonnet · balanced" },
  { value: "opus", label: "Opus · most capable" },
] as const;
export const MODEL_FALLBACK = [
  { value: "sonnet", label: "Sonnet" },
  { value: "opus", label: "Opus" },
  { value: "none", label: "No fallback" },
] as const;

export const TOOL_APPROVAL_MODES = [
  { value: "none", label: "Auto" },
  { value: "over-cap", label: "Approve over cap" },
  { value: "always", label: "Always approve" },
] as const;

export const TOOL_CATALOGUE: { id: AriaToolId; label: string; hint: string }[] = [
  { id: "order_lookup", label: "Order & shipment lookup", hint: "Look up orders, tracking, and delivery status." },
  { id: "returns", label: "Start a return", hint: "Open a return for an eligible order." },
  { id: "refund", label: "Issue a refund", hint: "Refund up to the guardrail ceiling without approval." },
  { id: "sub_change", label: "Change a subscription", hint: "Upgrade, downgrade, pause, or cancel." },
  { id: "address", label: "Update address", hint: "Change shipping or billing address on file." },
  { id: "booking", label: "Book an appointment", hint: "Schedule a service slot or callback." },
  { id: "invoice", label: "Send an invoice or receipt", hint: "Email a copy of a past invoice." },
  { id: "password", label: "Password & access reset", hint: "Trigger a reset link or unlock an account." },
  { id: "ticket", label: "Create a ticket", hint: "File a ticket for something the assistant can't resolve directly." },
  { id: "callback", label: "Schedule a callback", hint: "Queue a human callback at a chosen time." },
  { id: "escalate", label: "Escalate to a human", hint: "Hand off the conversation with a summary." },
  { id: "kb_write", label: "Suggest a knowledge article", hint: "Draft a KB entry from a resolved conversation." },
];

export const VARIANT_OPTIONS = TONE_PRESETS.filter((t) => t.value !== "custom");
